import React, { useEffect, useState } from 'react';
import { Container } from './styled';
import InternalHeader from '../../general/internalHeader/view';
import CommunityListItem from './communityListItem';
import { getStaffCommunities, createStaffCommunity } from 'api';
import { 
  Grid, 
  Button, 
  Modal, 
  TextField, 
  Box, 
  Typography, 
  IconButton, 
  Snackbar, 
  Alert, 
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router';
import communityBackground from 'assets/images/coworking.jpg';
import noAvatar from '../../../../assets/images/no-avatar.png';
import { useProfile } from 'context';
import { getAvatarUrl } from '../../../../api/utils';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const VISIBILITY_OPTIONS = [
  { value: 'PU', label: 'Public', description: 'Anyone can find and join this community' },
  { value: 'PR', label: 'Private', description: 'Visible to all, but joining requires approval' },
  { value: 'SE', label: 'Secret', description: 'Only visible to members, join by invitation' },
];

const CommunitiesManage = () => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const navigate = useNavigate();
  
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [communityName, setCommunityName] = useState('');
  const [communityDescription, setCommunityDescription] = useState('');
  const [communityVisibility, setCommunityVisibility] = useState('PU');
  const [contactEmail, setContactEmail] = useState('');
  const [requiresAdmission, setRequiresAdmission] = useState(false);
  const [creating, setCreating] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const fetchCommunities = async () => {
    try {
      setLoading(true);
      const response = await getStaffCommunities({
        page_size: 50,
        page_number: 1,
      });
      setCommunities(response.communities || []);
    } catch (error) {
      console.error('Error fetching communities:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to load communities');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const navigateToCommunity = (community) => {
    navigate(`/manage/community/${community?.slug}`);
  };

  const handleOpenAddModal = () => {
    // Pre-fill email from profile
    setContactEmail(profile?.email || '');
    setOpenAddModal(true);
  };

  const handleCreateCommunity = async () => {
    if (!communityName.trim()) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Community name is required');
      setSnackbarOpen(true);
      return;
    }

    try {
      setCreating(true);
      await createStaffCommunity({
        name: communityName,
        description: communityDescription,
        visibility: communityVisibility,
        contact_email: contactEmail,
        requires_admission_questions: requiresAdmission,
      });
      
      setOpenAddModal(false);
      resetForm();
      fetchCommunities();
      setSnackbarSeverity('success');
      setSnackbarMessage('Community created successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error creating community:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(error.response?.data?.error || 'Failed to create community');
      setSnackbarOpen(true);
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setCommunityName('');
    setCommunityDescription('');
    setCommunityVisibility('PU');
    setContactEmail('');
    setRequiresAdmission(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  useEffect(() => {
    fetchCommunities();
  }, []);

  const avatar = getAvatarUrl(profile, noAvatar);
  const canCreate = profile?.staff?.is_staff && 
    ['SA', 'AD', 'MA'].includes(profile?.staff?.role);
  const isSuperAdmin = profile?.staff?.role === 'SA';

  return (
    <Container>
      <InternalHeader 
        title={t('Your Communities')} 
        subtitle={t('Manage your communities and their members')} 
        avatar={avatar} 
        background={communityBackground} 
        actionTitle={canCreate ? t('Create Community') : ''}
        actionMethod={handleOpenAddModal} 
      />
      
      {/* Mobile Add Button */}
      {canCreate && (
        <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            color="primary"
            sx={{ color: 'white' }} 
            onClick={handleOpenAddModal}
            startIcon={<AddIcon />}
          >
            {t('Create Community')}
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : communities.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {t('No communities yet')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('Create your first community to get started')}
          </Typography>
          {canCreate && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleOpenAddModal}
              startIcon={<AddIcon />}
            >
              {t('Create Community')}
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
          {communities.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item.slug || index}>
              <CommunityListItem 
                community={item} 
                selectCommunity={navigateToCommunity} 
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Community Modal */}
      <Modal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, sm: 4 }
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 3, sm: 4 },
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}
        >
          <IconButton
            onClick={() => setOpenAddModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary'
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography variant="h5" component="h2" sx={{ fontWeight: 500, mb: 3 }}>
            {t('Create New Community')}
          </Typography>
          
          <TextField
            required
            label={t('Community Name')}
            value={communityName}
            onChange={(e) => setCommunityName(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            autoFocus
          />
          
          <TextField
            label={t('Description')}
            value={communityDescription}
            onChange={(e) => setCommunityDescription(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            multiline
            rows={3}
            placeholder={t('Describe what your community is about...')}
          />
          
          <TextField
            select
            required
            label={t('Visibility')}
            value={communityVisibility}
            onChange={(e) => setCommunityVisibility(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            helperText={VISIBILITY_OPTIONS.find(v => v.value === communityVisibility)?.description}
          >
            {VISIBILITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            label={t('Contact Email')}
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            type="email"
            placeholder={t('community@example.com')}
          />
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={requiresAdmission}
                  onChange={(e) => setRequiresAdmission(e.target.checked)}
                  color="primary"
                />
              }
              label={t('Require admission questions')}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {t('New members will need to answer questions before joining')}
            </Typography>
          </Box>
          
          {!isSuperAdmin && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.dark">
                {t('Your community will be created with pending status and require Super Admin approval.')}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 4
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setOpenAddModal(false);
                resetForm();
              }}
              sx={{ borderRadius: '20px' }}
              disabled={creating}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateCommunity}
              color="primary"
              sx={{ borderRadius: '20px' }}
              disabled={!communityName.trim() || creating}
            >
              {creating ? <CircularProgress size={24} /> : t('Create Community')}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CommunitiesManage;
